import React from 'react'
import ReactDOM from 'react-dom'
import store from 'store'
import {
  setIsShown,
  toggleDebugEnabled,
  updateDebug,
  hydrateAPIConfig,
  setMode,
  setSizeMode,
  setPosition,
  appendOptions,
} from 'redux/Config'
// module imports
import { setSIPDebug, unregister } from 'modules/Sip'
import { eventDispatch } from 'modules/GlobalEvents/Events'
import eventLogger from 'modules/Logger'
// constant imports
import { DISPATCH_EVENTS } from 'constants/GlobalEvents'

import { IApiConfig, IEncodedTokenData, IMountData } from 'domains/login'

import { vars } from 'vars'

import { DEV_LOGIN_DATA_KEY, getStorageJSON } from 'constants/LocalStorage'
import { DEV_MOUNT_POINT } from 'constants/General'
import { fetchInitData } from 'modules/Login'

import { subscribeToPermissionChanges } from './Permissions'

import { decodeStringEncodedBase64 } from './Helpers'

// main dialer window object
export class Dialer {
  private element?: HTMLElement

  // helper to check versions on fly for complicated bugs
  public checkVersion = '1.0.0'

  constructor() {
    // Notify others about dialer is created and ready to work with
    // Fire it in the next tick to make sure `dialer` is available when the event is fired
    setTimeout(() => {
      eventDispatch(DISPATCH_EVENTS.CONFIG.READY)
      this.initialize()
    })
  }

  async mount(element: HTMLElement, props?: IMountData): Promise<void> {
    if (this.element) {
      this.unmount()
    }

    if (props?.env) {
      eventLogger.initLogger(props.env || vars.env)
    }

    if (props?.apiConfig) {
      if (props?.apiConfig.STATIC_PATH) {
        const data = await this.fetchEnvJsonWithXHR(
          `${props?.apiConfig.STATIC_PATH}/env.json`,
        )

        window.FETCHED_DIALER_ENV_DATA = data as IApiConfig & {
          ENV: 'dev' | 'qa' | 'uat' | 'prod'
        }

        const envData = data as IApiConfig & {
          ENV: 'dev' | 'qa' | 'uat' | 'prod'
        }

        // change public path for dynamic imports
        // eslint-disable-next-line camelcase,no-undef
        __webpack_public_path__ =
          (envData.STATIC_PATH && this.normalizeBaseUrl(envData.STATIC_PATH)) ||
          (props.apiConfig.STATIC_PATH &&
            this.normalizeBaseUrl(props.apiConfig.STATIC_PATH)) ||
          '/'
      } else {
        // eslint-disable-next-line camelcase,no-undef
        __webpack_public_path__ = props.apiConfig.STATIC_PATH || '/'
      }

      store.dispatch(
        hydrateAPIConfig(
          props.env || vars.env,
          {
            ...props.apiConfig,
            env: vars.env,
            apiConfig: {
              ...props?.apiConfig,
              API_PUBLIC: props.apiConfig.API_PUBLIC,
              AUTH_URL: props.apiConfig.AUTH_URL,
              STATIC_PATH: props.apiConfig.STATIC_PATH,
            },
          },
          this.version(),
        ),
      )
    }

    if (props?.token) {
      const decodedData = decodeStringEncodedBase64(
        props.token,
      ) as unknown as IEncodedTokenData

      store.dispatch(
        fetchInitData({
          token: props.token,
          tokenType: 'JWT',
          accountId: decodedData.aid,
          userId: decodedData.uid,
        }),
      )
    }

    this.element = element

    const { default: App } = await import('App')

    ReactDOM.render(<App />, this.element)

    if (props?.mode) {
      this.applySizeMode(props)
    }
  }

  unmount(): void {
    this.element && ReactDOM.unmountComponentAtNode(this.element)
  }

  show(): void {
    store.dispatch(setIsShown(true))
  }

  hide(): void {
    store.dispatch(setIsShown(false))
  }

  toggle(): void {
    const { isShown } = store.getState().config
    isShown ? this.hide() : this.show()
  }

  toggleAnalyticDebug(): void {
    store.dispatch(
      updateDebug({
        googleAnalytic: !store.getState().config?.debug?.googleAnalytic,
      }),
    )
  }

  toggleDebug(): void {
    store.dispatch(toggleDebugEnabled())
  }

  setConsoleInfoDebug(value: boolean): void {
    eventLogger.setConsoleInfoDebug(value)
  }

  toggleTransportType(transportName: any): void {
    eventLogger.toggleTransportType(transportName)
  }

  toggleConsoleDebug(): void {
    eventLogger.toggleConsoleDebug()
  }

  stopSIP(): any {
    unregister()
  }

  toggleSIPDebug(): void {
    setSIPDebug(!localStorage.getItem('debug'))
  }

  version(): string | undefined {
    return process.env.REACT_APP_N2P_VERSION
  }

  private initialize() {
    // Additional initialization logic
    return Promise.all([subscribeToPermissionChanges()])
  }

  normalizeBaseUrl(baseUrl: string): string {
    if (!baseUrl.endsWith('/')) {
      return `${baseUrl}/`
    }

    return baseUrl
  }

  // INFO: need because webpack overrides fetch logic
  fetchEnvJsonWithXHR(url: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', url, true)
      xhr.responseType = 'json'

      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response)
        } else {
          reject(new Error(`Failed to load: ${xhr.status} ${xhr.statusText}`))
        }
      }

      xhr.onerror = function () {
        reject(new Error(`Network error while loading: ${url}`))
      }

      xhr.send()
    })
  }

  applySizeMode(props: IMountData) {
    switch (props.mode?.name) {
      case 'rcon': {
        store.dispatch(setMode(true))
        store.dispatch(setSizeMode(true))

        if (props?.mode?.position) {
          store.dispatch(setPosition(props?.mode.position))
        }

        break
      }
      case 'standard':
      default: {
        store.dispatch(setMode(false))
        break
      }
    }
  }
}

if (DEV_MOUNT_POINT) {
  setTimeout(() => {
    eventLogger.initLogger(vars.env.toUpperCase())

    window.dialer.mount(DEV_MOUNT_POINT!, {
      apiConfig: {
        API_PUBLIC: vars.apiPublic,
        API_UNITE: vars.apiUnite,
        AUTH_URL: vars.authUrl,
      },
      env: vars.env,
    })

    const devLoginData = getStorageJSON(DEV_LOGIN_DATA_KEY)

    if (devLoginData) {
      store.dispatch(
        appendOptions({ is3wayEnabled: true, isWarmTransferEnabled: true }),
      )
      store.dispatch(fetchInitData(devLoginData))
    }

    window.store = store
  }, 500)
}

export default Dialer
